import { createElement, useEffect, useMemo, useRef } from 'react';

import { App, Button, Space, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import type { StationSensorAlertRuleRow } from '@/features/admin/types/opsExtended.types';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';

import {
  acknowledgeOpenAlertsForIotStations,
  showIotAlertAckFeedback,
} from '../utils/acknowledgeOpenIotAlerts';
import { formatIotThresholdVisitDescription } from '../utils/formatIotThresholdVisitDescription';
import { isIotBreachSoundMuted, playIotThresholdBreachBeep } from '../utils/iotBreachBeep';

const STORAGE_KEY = 'iot-threshold-visit-v1';
const DEBOUNCE_MS = 320;

function loadShownIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function saveShownIds(ids: Set<string>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

/**
 * One notification per breached station id per session (sessionStorage), debounced batching,
 * optional beep (localStorage mute), notification actions: mute sound / acknowledge via API.
 */
export function useIoTThresholdVisitAlerts(
  stations: IoTDataPoint[],
  thresholdRows: StationSensorAlertRuleRow[],
  enabled: boolean,
) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const stationsRef = useRef(stations);
  const thresholdRowsRef = useRef(thresholdRows);

  const breachKey = useMemo(
    () =>
      stations
        .filter((s) => s.thresholdBreached)
        .map((s) => s.id)
        .sort()
        .join('|'),
    [stations],
  );

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIdsRef = useRef(new Set<string>());

  useEffect(() => {
    stationsRef.current = stations;
    thresholdRowsRef.current = thresholdRows;
    if (!enabled) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    if (!breachKey) {
      pendingIdsRef.current.clear();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    const breached = stations.filter((s) => s.thresholdBreached);
    const breachedIds = new Set(breached.map((s) => s.id));
    for (const id of [...pendingIdsRef.current]) {
      if (!breachedIds.has(id)) pendingIdsRef.current.delete(id);
    }
    const shown = loadShownIds();
    for (const s of breached) {
      if (!shown.has(s.id)) pendingIdsRef.current.add(s.id);
    }
    if (pendingIdsRef.current.size === 0) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const shownNow = loadShownIds();
      const latest = stationsRef.current;
      const toAlert = latest.filter(
        (s) => s.thresholdBreached && pendingIdsRef.current.has(s.id) && !shownNow.has(s.id),
      );
      pendingIdsRef.current.clear();
      if (toAlert.length === 0) return;

      const nextShown = loadShownIds();
      toAlert.forEach((s) => nextShown.add(s.id));
      saveShownIds(nextShown);

      if (!isIotBreachSoundMuted()) playIotThresholdBreachBeep();

      const description = formatIotThresholdVisitDescription(
        toAlert,
        thresholdRowsRef.current,
        t,
      );
      const key = `iot-threshold-${toAlert
        .map((s) => s.id)
        .sort()
        .join('-')}-${Date.now()}`;

      const ackAndClose = async () => {
        const result = await acknowledgeOpenAlertsForIotStations(toAlert);
        showIotAlertAckFeedback(message, t, result, toAlert);
        notification.destroy(key);
      };

      notification.warning({
        key,
        message: t('realtime.iotThresholdVisit.title'),
        description,
        duration: 0,
        placement: 'topRight',
        btn: createElement(
          Space,
          { size: 'small' },
          createElement(
            Button,
            {
              size: 'small',
              onClick: () => {
                notification.destroy(key);
              },
            },
            t('common.close', { defaultValue: 'Đóng' }),
          ),
          createElement(
            Button,
            {
              type: 'primary',
              size: 'small',
              onClick: () => {
                void ackAndClose();
              },
            },
            t('realtime.iotThresholdVisit.acknowledge'),
          ),
        ),
      });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [enabled, breachKey, message, stations, thresholdRows, t]);
}
